import { ChangeDetectionStrategy, Component, computed, EventEmitter, input, Output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme, IconPosition, NotificationScale, NotificationTheme } from '@fcom/ui-components';
import { Amount, ServiceCatalogService } from '@fcom/dapi/interfaces';
import { FinnairPassengerItem } from '@fcom/dapi/api/models';

import { NonSeatBundleAvailability } from '../../../../../store';
import { getPassengerTierLevel } from '../../../utils';

/**
 * One passenger's offer for an ancillary+ancillary (non-seat) bundle: the passenger name and
 * "Add combo" action, the bundle's constituent service cards and the passenger-specific price. When the
 * bundle isn't available for this passenger on this flight (`availability` null/unavailable), the
 * passenger is still listed but the offer is replaced by a short "not available" notice.
 */
@Component({
  selector: 'fin-bundle-offer-card',
  templateUrl: './bundle-offer-card.component.html',
  styleUrls: ['./bundle-offer-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BundleOfferCardComponent {
  readonly constituents = input.required<ServiceCatalogService[]>();
  readonly passenger = input.required<FinnairPassengerItem>();
  readonly availability = input.required<NonSeatBundleAvailability | null>();
  readonly selectedForPassenger = input.required<boolean>();
  readonly isBundleAncillariesIncludedInTier = input<boolean>(false);

  @Output() addCombo = new EventEmitter<void>();
  @Output() removeCombo = new EventEmitter<void>();

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly NotificationTheme = NotificationTheme;
  readonly NotificationScale = NotificationScale;

  readonly passengerName = computed(() =>
    [this.passenger()?.firstName, this.passenger()?.lastName].filter(Boolean).join(' ')
  );
  readonly tierLevel = computed(() => getPassengerTierLevel(this.passenger().id, [this.passenger()]));

  /**
   * A zero price in the combo's currency, shown when the combo is free via tier benefit. The currency is
   * taken from the constituent catalog services (or another passenger's combo price) so it is not hardcoded.
   */
  readonly freePrice = computed<Amount | null>(() => {
    const currencyCode =
      this.constituents()?.[0]?.totalPrice?.currencyCode ?? this.availability()?.bundleTotalPrice?.currencyCode;
    return currencyCode ? { currencyCode, amount: '0' } : null;
  });
}
